import type { IConnectProviderOptions, ProviderName } from '@interfaces/IAuth';
import type { MfaChallengeMethod, MfaOtpChannel } from '@interfaces/IMfa';
import { isMfaOtpChannel, MFA_CHALLENGE_METHODS, MFA_OTP_CHANNELS } from '@interfaces/IMfa';
import type { UnifiedProject } from '@interfaces/IUnifiedProject';
import { ProviderErrorResponse, UnauthorizedError } from '@d-orca/shared/client/apis';
import { parseFeatures, type Feature } from '@d-orca/shared/permissions';
import { notifyRateLimited } from '@apis/rateLimited';

/**
 * Cookie-based sessions. Trusstor is the identity provider, but the browser
 * never holds a token: the D-ORCA backend sets an httpOnly session cookie on
 * login and the browser sends it automatically on every request that opts into
 * `credentials: 'include'`. There is NO client-side token storage and NO
 * Authorization-header injection here — auth state is derived from
 * `GET /api/auth/me`, not from anything readable in JS.
 *
 * See docs/specs/2026-07-21-trusstor-identity-provider-auth-design.md
 */

const BFF_BASE_URL = import.meta.env.VITE_BFF_BASE_URL ?? 'http://localhost:3030';

// Thrown by shared API factories too, so the class identity lives in
// @d-orca/shared/client — re-exported here to keep the historical import site.
export { ProviderErrorResponse, UnauthorizedError };

/** Neutral login failure — deliberately does NOT distinguish "no such user"
 *  from "wrong password" (enumeration-safe). */
export class InvalidCredentialsError extends Error {
  constructor() { super('invalid email or password'); this.name = 'InvalidCredentialsError'; }
}

/** The address signs in with its company account (`403 SSO_REQUIRED`) — not a wrong password. */
export class SsoRequiredError extends Error {
  constructor() { super('this account signs in with its company account'); this.name = 'SsoRequiredError'; }
}

/** The set-password token in the emailed link is missing, invalid, or expired. */
export class InvalidSetPasswordTokenError extends Error {
  constructor(message = 'invalid or expired token') {
    super(message);
    this.name = 'InvalidSetPasswordTokenError';
  }
}

// Genba's token endpoint only mints tokens for prime-contractor users; a
// non-prime login is authenticated but forbidden (backend maps it to 403).
export class GenbaNotPrimeUserError extends Error {
  constructor() {
    super('genba login must be a prime-contractor user');
    this.name = 'GenbaNotPrimeUserError';
  }
}

// The Genba API root URL was missing, malformed, or rejected by the backend's
// host allowlist (SSRF guard). Carries the backend message for display.
export class GenbaRootUrlError extends Error {
  constructor(message = 'invalid genba api root url') {
    super(message);
    this.name = 'GenbaRootUrlError';
  }
}

export type AuthUser = {
  id: string;
  username: string;
  orgId: string;
  trusstorUserId?: string;
  genbaUserId?: string;
  /** Upstream login identifiers bound to this user; prefill the connect form. */
  trusstorLoginId?: string;
  genbaLoginId?: string;
  /** Genba tenant root URL bound to this user; prefills the connect form. */
  genbaRootUrl?: string;
  connectedProviders: ProviderName[];
  /**
   * Whether two-factor auth is armed on this account. Drives the security
   * settings section's status + enable/disable affordance.
   *
   * NOT part of the pinned MFA proxy contracts — there is no status endpoint
   * and `/api/auth/me` isn't specified to carry it. Absent ⇒ the UI reads
   * "disabled", so a user who already enrolled would be shown the wrong state
   * until the backend surfaces this. Flagged for the backend, not invented
   * here: nothing else in the flow depends on it.
   */
  mfaEnabled?: boolean;
  /**
   * How this session signed in. `sso` users have no d-orca password or MFA —
   * their company's sign-in owns both — so those settings are hidden. Absent on
   * older sessions, which were all password logins.
   */
  authMethod?: 'password' | 'sso';
  /**
   * What the user's role grants, resolved by the server. The UI gates on this
   * list and never maps a role to rights itself; the routes enforce every
   * feature regardless (role-permissions spec, rulings 3a and 7). Read
   * leniently: a name this build doesn't know is dropped, so a newer server
   * can't break login.
   */
  features: Feature[];
};

// Fires once per "the session ended" transition. The browser holds no token to
// refresh, so a session 401 from any authenticated data call is terminal: we
// dispatch `dorca:auth-expired` exactly once so AuthContext can clear state and
// bounce to /login. The guard keeps concurrent 401s from firing the event N
// times (analytics / programmatic navigate listeners must run once).
let authExpiredNotified = false;

function notifyAuthExpired(): void {
  if (authExpiredNotified) return;
  authExpiredNotified = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('dorca:auth-expired'));
  }
}

/**
 * What a 401 means, given the error code that came with it. Exported because
 * `authedFetch` can't serve every caller — an upload that reports progress has
 * to use XMLHttpRequest, and fetch exposes no upload progress — and this policy
 * must not be re-derived per transport. An upstream-provider reauth rides the
 * same status on a different credential, so it must NOT end the D-ORCA session.
 */
export function applyUnauthorizedPolicy(errorCode: string | undefined): void {
  if (errorCode === 'PROVIDER_REAUTH_REQUIRED' || errorCode === 'UPSTREAM_UNAUTHORIZED' || errorCode === 'INVALID_CREDENTIALS') return;
  notifyAuthExpired();
}

type ErrorBody = {
  error?: { code?: string; message?: unknown; details?: { provider?: ProviderName } };
} | null;

/** Narrow an unknown value to ErrorBody. Accepts null and objects with an optional `error` key. */
function isErrorBody(v: unknown): v is ErrorBody {
  if (v === null) return true;
  if (!isRecord(v)) return false;
  if (!('error' in v)) return true;
  return typeof v.error === 'object';
}

// Shared error translator for the auth endpoints. `preParsed` lets a caller that
// already consumed the body (a Response body can only be read once) hand it in.
async function parseError(res: Response, preParsed?: unknown): Promise<never> {
  const raw = preParsed !== undefined ? preParsed : await res.json().catch(() => null);
  const body: ErrorBody = isErrorBody(raw) ? raw : null;
  const code = body?.error?.code;
  if (res.status === 401) {
    if (code === 'INVALID_CREDENTIALS') throw new InvalidCredentialsError();
    const provider = body?.error?.details?.provider;
    if (code === 'PROVIDER_REAUTH_REQUIRED' && provider) {
      throw new ProviderErrorResponse(code, provider);
    }
    throw new UnauthorizedError();
  }
  if (res.status === 409) {
    const provider = body?.error?.details?.provider;
    if (provider && (code === 'PROVIDER_NOT_CONNECTED' || code === 'PROVIDER_DISCONNECTED')) {
      throw new ProviderErrorResponse(code, provider);
    }
  }
  const message = typeof body?.error?.message === 'string' ? body.error.message : 'request failed';
  throw new Error(`${res.status} ${res.statusText}: ${message}`);
}

/**
 * Plain credentialed fetch for the auth surfaces (login / me / logout /
 * forgot-password / set-password). It sends the session cookie but does NOT
 * dispatch `dorca:auth-expired` on 401 — a 401 here is an expected outcome
 * (invalid credentials, not-yet-logged-in bootstrap) that the caller
 * interprets, not a mid-session expiry.
 */
async function credFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers ?? {});
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(`${BFF_BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
}

/**
 * Credentialed fetch for authenticated data calls. The session cookie rides
 * along automatically (`credentials: 'include'`); no Authorization header is
 * attached. A session 401 is terminal — there is no client token to refresh —
 * so we fire `dorca:auth-expired` once and hand the response back for the
 * caller to surface as an error. Upstream-provider 401s (Genba needs a
 * reconnect, Trusstor token rejected) ride the same status but a different
 * credential; those are handed straight back WITHOUT ending the D-ORCA session.
 * A 429 is handed back too, after announcing `dorca:rate-limited` so the
 * app-wide notice can say why the action failed.
 */
async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers ?? {});
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${BFF_BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
  if (res.status === 429) notifyRateLimited(res);
  if (res.status !== 401) return res;

  // Peek via a clone so `res` stays readable by the caller.
  const code = await res.clone().json().then((b) => b?.error?.code).catch(() => undefined);
  applyUnauthorizedPolicy(code);
  return res;
}

/** The profile fields the guard checks; `features` is read leniently apart. */
type ServerAuthUser = Omit<AuthUser, 'features'> & { features?: unknown };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Optional user fields may be absent, but when present must be strings. */
function isOptionalString(v: unknown): v is string | undefined {
  return v === undefined || typeof v === 'string';
}

function isOptionalBoolean(v: unknown): v is boolean | undefined {
  return v === undefined || typeof v === 'boolean';
}

/**
 * Structural check for the user object the auth endpoints return. Covers the
 * fields the app actually reads: identity (`id`, `username`, `orgId`), the
 * provider list, and the connect-form prefills. Permissive about extra fields
 * so additive backend changes don't break the client.
 */
function isServerAuthUser(v: unknown): v is ServerAuthUser {
  return (
    isRecord(v) &&
    typeof v.id === 'string' &&
    typeof v.username === 'string' &&
    typeof v.orgId === 'string' &&
    Array.isArray(v.connectedProviders) &&
    v.connectedProviders.every((p) => p === 'trusstor' || p === 'genba') &&
    isOptionalString(v.trusstorUserId) &&
    isOptionalString(v.genbaUserId) &&
    isOptionalString(v.trusstorLoginId) &&
    isOptionalString(v.genbaLoginId) &&
    isOptionalString(v.genbaRootUrl) &&
    isOptionalBoolean(v.mfaEnabled)
  );
}

/**
 * Pull the profile out of a /login or /me body. The pinned contract says these
 * endpoints "return the user profile"; we tolerate either a `{ user }` envelope
 * (matching the legacy shape) or a bare profile object so a small backend shape
 * choice doesn't break the client.
 */
function toAuthUser(user: ServerAuthUser): AuthUser {
  return { ...user, features: parseFeatures(user.features) };
}

function extractServerUser(data: unknown, ctx: string): AuthUser {
  if (isRecord(data) && isServerAuthUser(data.user)) return toAuthUser(data.user);
  if (isServerAuthUser(data)) return toAuthUser(data);
  throw new Error(`${ctx}: response did not contain a valid user profile`);
}

/** The piggybacked project list is optional; element shape is the server's
    contract (UnifiedProjectSchema) — only the container is checked here.
    Shared with the MFA endpoints, which return the same login payload. */
export function optionalProjects(data: unknown): UnifiedProject[] | undefined {
  if (!isRecord(data)) return undefined;
  return Array.isArray(data.projects) ? (data.projects as UnifiedProject[]) : undefined;
}

/**
 * Turn a response body that carries a user profile into the client-side
 * `AuthUser` and arm the expiry dispatcher for the new session. Shared by
 * `/login` and the MFA endpoints that promote `dorca_pending` to a real
 * session (`mfa/verify`, `mfa/enroll/confirm`) so the profile parse and the
 * `authExpiredNotified` reset live in exactly one place.
 */
export function adoptSessionUser(data: unknown, ctx: string): AuthUser {
  const user = extractServerUser(data, ctx);
  // A fresh session — allow a future expiry to dispatch `dorca:auth-expired`.
  authExpiredNotified = false;
  return user;
}

/**
 * The three shapes `POST /api/auth/login` can answer with (all HTTP 200 — the
 * MFA outcomes are successful *partial* authentications, not errors):
 *  - `{user}`        → full session, `dorca_sid` set;
 *  - `{mfaRequired}` → `dorca_pending` set, challenge the listed factors;
 *  - `{enrollmentRequired}` → `dorca_pending` set, force enrollment first.
 */
export type LoginOutcome =
  | { kind: 'authenticated'; user: AuthUser; projects?: UnifiedProject[] }
  | {
      kind: 'mfa-required';
      methods: MfaChallengeMethod[];
      /** Channels the code may be sent on, and the default. Absent ⇒ no picker. */
      otpChannels?: MfaOtpChannel[];
      primaryOtpChannel?: MfaOtpChannel;
    }
  | { kind: 'enrollment-required' };

/** Narrow the contract's channel list defensively — an unknown channel is dropped, not offered. */
function parseOtpChannels(v: unknown): MfaOtpChannel[] {
  if (!Array.isArray(v)) return [];
  return MFA_OTP_CHANNELS.filter((c) => v.includes(c));
}

/** Narrow the contract's `methods: ('totp'|'otp')[]` defensively. */
function parseChallengeMethods(v: unknown): MfaChallengeMethod[] {
  if (!Array.isArray(v)) return [];
  return MFA_CHALLENGE_METHODS.filter((m) => v.includes(m));
}

/**
 * POST /api/auth/login {email,password}. On a fully authenticated login the
 * backend sets the httpOnly session cookie and returns the user profile
 * (optionally with the project list); when the account is MFA-protected it
 * instead sets `dorca_pending` and returns the challenge / enrollment marker.
 * A 401 is a neutral failure — surfaced as `InvalidCredentialsError` without
 * distinguishing user-not-found.
 */
export async function login(email: string, password: string): Promise<LoginOutcome> {
  const res = await credFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (res.status === 401) throw new InvalidCredentialsError();
  if (res.status === 403) {
    const body: unknown = await res.json().catch(() => null);
    if (isRecord(body) && isRecord(body.error) && body.error.code === 'SSO_REQUIRED') throw new SsoRequiredError();
    await parseError(res, body);
  }
  if (!res.ok) await parseError(res);
  const data: unknown = await res.json();
  if (isRecord(data)) {
    if (data.mfaRequired === true) {
      const methods = parseChallengeMethods(data.methods);
      // An empty/unrecognised method list would strand the user on a challenge
      // with nothing to submit — treat it as a protocol failure, not a screen.
      if (methods.length === 0) throw new Error('login: mfaRequired with no usable method');
      const otpChannels = parseOtpChannels(data.otpChannels);
      return {
        kind: 'mfa-required',
        methods,
        ...(otpChannels.length > 0 ? { otpChannels } : {}),
        ...(isMfaOtpChannel(data.primaryOtpChannel) ? { primaryOtpChannel: data.primaryOtpChannel } : {}),
      };
    }
    if (data.enrollmentRequired === true) return { kind: 'enrollment-required' };
  }
  return {
    kind: 'authenticated',
    user: adoptSessionUser(data, 'login'),
    projects: optionalProjects(data),
  };
}

/**
 * GET /api/auth/me — the source of truth for auth state. Returns the current
 * profile, or throws `UnauthorizedError` on 401 (not logged in / session
 * expired). Used to bootstrap auth on app load and by route guards.
 */
export async function fetchMe(signal?: AbortSignal): Promise<AuthUser> {
  const res = await credFetch('/api/auth/me', { signal });
  if (!res.ok) await parseError(res);
  const data: unknown = await res.json();
  return extractServerUser(data, 'fetchMe');
}

/** POST /api/auth/logout — clears the server session (and the cookie).
 *  Best-effort: a network failure still lets the client drop local state. */
export async function logout(): Promise<void> {
  try {
    await credFetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // Ignore — the caller clears client state regardless.
  }
}

/**
 * POST /api/auth/forgot-password {email}. The backend always answers 200 (never
 * reveals whether the account exists), so this resolves regardless of outcome —
 * the UI shows the same neutral confirmation either way.
 */
export async function forgotPassword(email: string): Promise<void> {
  try {
    await credFetch('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  } catch {
    // Swallow network errors too: the confirmation copy is intentionally
    // outcome-independent, so surfacing an error would leak signal / add noise.
  }
}

/**
 * POST /api/auth/set-password {token,password}. Used for BOTH first-time invite
 * and password reset (same flow). Throws `InvalidSetPasswordTokenError` when the
 * token is missing/invalid/expired (400/401/410) so the page can show the
 * "link expired" state; other failures throw a generic Error.
 */
export async function setPassword(token: string, password: string): Promise<void> {
  const res = await credFetch('/api/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
  if (res.ok) return;
  if (res.status === 400 || res.status === 401 || res.status === 410) {
    const raw: unknown = await res.json().catch(() => null);
    const body: ErrorBody = isErrorBody(raw) ? raw : null;
    const msg = typeof body?.error?.message === 'string' ? body.error.message : undefined;
    throw new InvalidSetPasswordTokenError(msg);
  }
  await parseError(res);
}

export type ProviderConnState = 'ok' | 'not-connected' | 'disconnected';

/** Upstream provider connection status (the TopBar seeds the reconnect prompt
 *  from this on load). `genbaExpiresAt` is Genba's session expiry (ISO) or null
 *  when not connected — the TopBar arms a single reconnect timer for it. */
export interface IProvidersStatus {
  genba: ProviderConnState;
  trusstor: ProviderConnState;
  genbaExpiresAt: string | null;
}

export async function fetchProvidersStatus(signal?: AbortSignal): Promise<IProvidersStatus> {
  const res = await authedFetch('/api/auth/providers', { signal });
  if (!res.ok) await parseError(res);
  const data: unknown = await res.json();
  if (typeof data !== 'object' || data === null) {
    throw new Error('fetchProvidersStatus: malformed response');
  }
  const record = data as Record<string, unknown>;
  const parse = (key: 'genba' | 'trusstor'): ProviderConnState => {
    const value = record[key];
    if (value !== 'ok' && value !== 'not-connected' && value !== 'disconnected') {
      throw new Error(`fetchProvidersStatus: unexpected ${key} status`);
    }
    return value;
  };
  const genbaExpiresAt = typeof record.genbaExpiresAt === 'string' ? record.genbaExpiresAt : null;
  return { genba: parse('genba'), trusstor: parse('trusstor'), genbaExpiresAt };
}

export async function connectProvider(
  provider: ProviderName,
  username: string,
  password: string,
  opts?: IConnectProviderOptions,
): Promise<void> {
  // Genba is the per-user connectable provider (decoupled from the Trusstor
  // login): its connect body is `{ genbaUserId, password, rootUrl }` — the
  // rootUrl encodes the tenant and is the base for the token call. `username`
  // here is the Genba login ID. Trusstor is the identity provider (login), not
  // a connectable provider, so its legacy body shape is left as-is.
  const reqBody =
    provider === 'genba'
      ? { genbaUserId: username, password, ...(opts?.rootUrl ? { rootUrl: opts.rootUrl } : {}) }
      : { username, password };
  const res = await authedFetch(`/api/auth/connect/${provider}`, {
    method: 'POST',
    body: JSON.stringify(reqBody),
  });
  if (res.ok) return;
  // Genba connect-specific failures are handled here (not in the shared
  // parseError) so that helper stays generic. Read the body once, then defer
  // to parseError for the shared cases.
  const rawErrBody: unknown = await res.json().catch(() => null);
  const errBody: ErrorBody = isErrorBody(rawErrBody) ? rawErrBody : null;
  const code = errBody?.error?.code;
  if (res.status === 403 && code === 'GENBA_NOT_PRIME_USER') throw new GenbaNotPrimeUserError();
  if (res.status === 400 && (code === 'GENBA_ROOT_URL_REQUIRED' || code === 'GENBA_ROOT_URL_INVALID')) {
    const msg = errBody?.error?.message;
    throw new GenbaRootUrlError(typeof msg === 'string' ? msg : undefined);
  }
  await parseError(res, errBody);
}

export async function disconnectProvider(provider: ProviderName): Promise<void> {
  const res = await authedFetch(`/api/auth/connect/${provider}`, { method: 'DELETE' });
  if (!res.ok) await parseError(res);
}

export { authedFetch, credFetch, BFF_BASE_URL };
